import { errMissingEntityMetrics } from "@/app/errors/errors.graph_calculations"
import { mainLogger as log } from "@/app/logger"
import { EntityValues, GroupMemberValues, Metric, Term } from "@/app/types"

/**
 * Sorts an array of group member values by the specified sort column and direction.
 * @param members - the array of group member values to sort.
 * @param isAscending - a boolean value indicating whether to sort in ascending or descending order.
 * @param sortColumn - the sort column.
 * @param isFund - a boolean value indicating if current selected entity is a fund.
 * @param metric - the metric type.
 * @param term - the term type.
 * @param isOverviewPage - Optional - indicates whether you are in the overview page or not, some logics change based on this condition.
 * @returns the sorted array of group member values.
 */
export function sortMembersColumns(
  members: GroupMemberValues[],
  isAscending: Boolean,
  sortColumn: string,
  isFund: boolean,
  metric: Metric,
  term: Term,
  isOverviewPage?: Boolean
) {
  return members.toSorted((memberA, memberB) => {
    if (sortColumn === "value") {
      const getValue = (member: GroupMemberValues) => {
        return isOverviewPage ?
            member[term.interval].ranking[term.key][metric.name]
          : member.ranking?.[term.key]?.[metric.name] ?? null
      }
      const valueA = getValue(memberA)
      const valueB = getValue(memberB)

      if (valueA === null && valueB === null) {
        return 0
      } else if (valueA === null) {
        return 1
      } else if (valueB === null) {
        return -1
      }

      const valueComparison = isAscending ? valueA - valueB : valueB - valueA

      if (valueComparison === 0) {
        // using alphabetical order if numbers are the same
        const nameA =
          isFund ? memberA.fund?.name ?? "" : memberA.firm?.name ?? ""
        const nameB =
          isFund ? memberB.fund?.name ?? "" : memberB.firm?.name ?? ""

        return nameA.localeCompare(nameB)
      }

      return valueComparison
    }

    if (isFund) {
      if (isOverviewPage) {
        return isAscending ?
            memberA.fund?.name?.localeCompare(memberB.fund?.name ?? "") ?? 0
          : memberB.fund?.name?.localeCompare(memberA.fund?.name ?? "") ?? 0
      }
      return isAscending ?
          memberA.fund?.name?.localeCompare(memberB.fund?.name ?? "") ?? 0
        : memberB.fund?.name?.localeCompare(memberA.fund?.name ?? "") ?? 0
    } else {
      return isAscending ?
          memberA.firm?.name?.localeCompare(memberB.firm?.name ?? "") ?? 0
        : memberB.firm?.name?.localeCompare(memberA.firm?.name ?? "") ?? 0
    }
  })
}

/**
 * Returns an array of available metrics based on the chart type and entity metrics.
 * @param isFund - type of the current entity.
 * @param entityMetrics - the entity metrics.
 * @param metricList - the list of metrics to filter.
 * @param term - the term type.
 * @returns an array of available metrics.
 */
export function availableMetricsArrayFilter(
  isFund: boolean,
  entityMetrics: EntityValues | null,
  metricList: Metric[],
  term: Term
) {
  let filteredKeys: string[] = []
  const metricNames = metricList.map((metric) => metric.text)
  const metricTexts = metricList.map((metric) => metric.name)

  if (!entityMetrics) {
    errMissingEntityMetrics(isFund, {
      "Term Key": term.key,
      "Term Interval": term.interval,
      "List Of Metrics": metricList,
    })
    return
  }

  if (entityMetrics && !entityMetrics.ranking[term.key]) {
    log.warn(
      "[ERR-GC-19]",
      `term key: ${term.key} for the selected term interval: ${term.interval} of the term: ${term} in ${term.isPeriodic ? "Periodic" : "Ranking"} values`,
      {
        Entity: {
          id: entityMetrics.fund?.fundId || entityMetrics.firm?.firmId,
          name: entityMetrics.fund?.name || entityMetrics.firm?.name,
        },
        "Metrics Available": metricNames,
        "Term Interval Keys Available for Ranking Values": Object.keys(
          entityMetrics.ranking
        ),
        Term: term,
      }
    )
  }

  filteredKeys = Object.keys(entityMetrics?.ranking?.[term.key] ?? {}).filter(
    (key) => {
      return metricList.some((metric) => key === metric.name)
    }
  )

  // returns some metrics even if there is no match for current entity values
  if (filteredKeys.length === 0) filteredKeys = metricTexts

  return filteredKeys.filter((key) => key !== Metric.PrevAUM)
}

export function applyRankPropertyInMembers(
  sortedMembers: GroupMemberValues[],
  termKey: string,
  metric: string
): GroupMemberValues[] {
  const getId = (member: GroupMemberValues) =>
    member.fund?.fundId ?? member.firm?.firmId

  // filter members with valid numeric values
  const validMembers = sortedMembers.filter((member) => {
    const value = member.ranking?.[termKey]?.[metric]
    return typeof value === "number" && !isNaN(value)
  })

  // assign ranks
  const rankedMap = new Map<string, GroupMemberValues>()
  validMembers.forEach((member, idx) => {
    const id = getId(member)
    if (id) {
      rankedMap.set(id, { ...member, rank: idx + 1 })
    }
  })

  // return all members
  return sortedMembers.map((member) => {
    const id = getId(member)
    const ranked = id ? rankedMap.get(id) : undefined
    return ranked ?? { ...member, rank: "-" }
  })
}

export function getDeltaColorClass(deltaValue: number | null) {
  if (deltaValue === null) return ""
  if (deltaValue > 0) return "text-main-trend-up"
  if (deltaValue < 0) return "text-main-trend-down"
}

export function getMemberCountText(
  isFiltered: boolean,
  sortedMembers: GroupMemberValues[]
) {
  const totalMembers = sortedMembers.length - 1 // excluding main entity

  const membersFiltered = sortedMembers.reduce((acc, curr) => {
    if (curr.filterIncluded) return acc + 1
    else return acc
  }, 0)

  if (!isFiltered) {
    return `(${totalMembers})`
  }

  // if filtered but all members are included
  if (membersFiltered === sortedMembers.length) {
    return `(${membersFiltered - 1})`
  }

  // show X out of Y format
  return `(${membersFiltered - 1} out of ${totalMembers})`
}

export function getDeltaValueFromComparison(
  value: number | null,
  comparisonValue: number | null | undefined
): number | string {
  if (value == null || comparisonValue == null) return "-"

  if (comparisonValue === 0) return value

  return value - comparisonValue
}
